// Currency configuration and utilities

export interface CurrencyConfig {
  symbol: string;
  label: string;
}

export const currencyDisplay: Record<string, CurrencyConfig> = {
  usd: { symbol: '$', label: 'USD' },
  eur: { symbol: '€', label: 'EUR' },
  gbp: { symbol: '£', label: 'GBP' },
  cad: { symbol: 'C$', label: 'CAD' },
  aud: { symbol: 'A$', label: 'AUD' },
  nzd: { symbol: 'NZ$', label: 'NZD' },
  sgd: { symbol: 'S$', label: 'SGD' },
  jpy: { symbol: '¥', label: 'JPY' },
  chf: { symbol: 'CHF', label: 'CHF' },
  sek: { symbol: 'kr', label: 'SEK' },
  nok: { symbol: 'kr', label: 'NOK' },
  dkk: { symbol: 'kr', label: 'DKK' },
  brl: { symbol: 'R$', label: 'BRL' },
  mxn: { symbol: 'MX$', label: 'MXN' },
  zar: { symbol: 'R', label: 'ZAR' },
};

// Minor unit factor: how many minor units per major unit
// Most currencies use 100 (cents), but JPY uses 1 (no decimals)
export const minorUnitFactor: Record<string, number> = {
  usd: 100,
  eur: 100,
  gbp: 100,
  cad: 100,
  aud: 100,
  nzd: 100,
  sgd: 100,
  chf: 100,
  sek: 100,
  nok: 100,
  dkk: 100,
  brl: 100,
  mxn: 100,
  zar: 100,
  jpy: 1, // no decimals
};

/**
 * Convert minor units to major units
 * @param minor - Amount in minor units (e.g., cents for USD, yen for JPY)
 * @param currency - Currency code (lowercase)
 * @returns Amount in major units
 */
export function formatAmount(minor: number, currency: string): number {
  const factor = minorUnitFactor[currency.toLowerCase()] ?? 100;
  return minor / factor;
}

/**
 * Convert major units to minor units
 * @param major - Amount in major units (e.g., dollars for USD, yen for JPY)
 * @param currency - Currency code (lowercase)
 * @returns Amount in minor units
 */
export function toMinorUnits(major: number, currency: string): number {
  const factor = minorUnitFactor[currency.toLowerCase()] ?? 100;
  return Math.round(major * factor);
}

/**
 * Format currency amount with proper symbol and decimals
 * @param minor - Amount in minor units
 * @param currency - Currency code (lowercase)
 * @returns Formatted currency string
 */
export function formatCurrency(minor: number, currency = 'usd'): string {
  const currencyLower = currency.toLowerCase();
  const major = formatAmount(minor, currencyLower);
  const config = currencyDisplay[currencyLower];

  if (!config) {
    // Fallback to USD if currency not found
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(major);
  }

  // Use Intl.NumberFormat for proper formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyLower.toUpperCase(),
  }).format(major);
}

/**
 * Format an amount already in major units (e.g. analytics revenue, which the
 * backend reports in dollars per Whop's API).
 */
export function formatMajorCurrency(major: number, currency = 'usd'): string {
  return formatCurrency(toMinorUnits(major, currency), currency);
}

/**
 * Get currency symbol
 * @param currency - Currency code (lowercase)
 * @returns Currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  const config = currencyDisplay[currency.toLowerCase()];
  return config?.symbol ?? '$';
}

/**
 * Get currency label
 * @param currency - Currency code (lowercase)
 * @returns Currency label (uppercase code)
 */
export function getCurrencyLabel(currency: string): string {
  const config = currencyDisplay[currency.toLowerCase()];
  return config?.label ?? 'USD';
}

/**
 * Get list of supported currencies for dropdown
 * @returns Array of currency options with code and label
 */
export function getSupportedCurrencies(): Array<{ value: string; label: string; symbol: string }> {
  return Object.entries(currencyDisplay).map(([code, config]) => ({
    value: code.toUpperCase(),
    label: `${config.label} (${config.symbol})`,
    symbol: config.symbol,
  }));
}

/**
 * Get minimum price in minor units for a currency
 * Most currencies require minimum $1.00 (100 cents)
 * JPY requires minimum ¥100 (100 yen, since factor is 1)
 */
export function getMinimumPrice(currency: string): number {
  const currencyLower = currency.toLowerCase();
  const factor = minorUnitFactor[currencyLower] ?? 100;

  // For JPY (factor=1), minimum is 100 yen (stored as 100)
  // For USD (factor=100), minimum is $1.00 = 100 cents (stored as 100)
  return factor === 1 ? 100 : 100;
}

/**
 * Get the number of decimal places for a currency
 * @param currency - Currency code
 * @returns Number of decimal places (0 for JPY, 2 for most others)
 */
export function getDecimalPlaces(currency: string): number {
  const factor = minorUnitFactor[currency.toLowerCase()] ?? 100;
  return factor === 1 ? 0 : 2;
}

/**
 * Get the step value for price inputs based on currency
 * @param currency - Currency code
 * @returns Step value for input (1 for JPY, 0.01 for most others)
 */
export function getPriceInputStep(currency: string): string {
  const factor = minorUnitFactor[currency.toLowerCase()] ?? 100;
  return factor === 1 ? '1' : '0.01';
}

/**
 * Get minimum price in major units for a currency
 * @param currency - Currency code
 * @returns Minimum price in major units (e.g., 1.00 for USD, 100 for JPY)
 */
export function getMinimumPriceMajor(currency: string): number {
  const minorMin = getMinimumPrice(currency);
  return formatAmount(minorMin, currency);
}
